import { useState, useRef, useCallback, useEffect } from 'react';

interface SpeechRecognitionOptions {
  silenceTimeoutMs?: number;
  onSilence?: (text: string) => void;
  continuous?: boolean;
}

interface SpeechRecognitionHook {
  transcript: string;
  interimTranscript: string;
  isListening: boolean;
  isSpeaking: boolean;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
  resetTranscript: () => void;
}

export function useSpeechRecognition(
  options: SpeechRecognitionOptions = {},
): SpeechRecognitionHook {
  const { silenceTimeoutMs = 2000, onSilence, continuous = false } = options;

  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accumulatedRef = useRef('');
  const stoppedManuallyRef = useRef(false);
  const onSilenceRef = useRef(onSilence);
  onSilenceRef.current = onSilence;
  const silenceTimeoutMsRef = useRef(silenceTimeoutMs);
  silenceTimeoutMsRef.current = silenceTimeoutMs;

  const isSupported =
    typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const startSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (!onSilenceRef.current) return;
    silenceTimerRef.current = setTimeout(() => {
      const text = accumulatedRef.current.trim();
      if (text) {
        onSilenceRef.current?.(text);
        accumulatedRef.current = '';
        setTranscript('');
      }
      setIsSpeaking(false);
    }, silenceTimeoutMsRef.current);
  }, [clearSilenceTimer]);

  const start = useCallback(() => {
    if (!isSupported || recognitionRef.current) return;

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return;
    const recognition = new Ctor();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    stoppedManuallyRef.current = false;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (interim) {
        setIsSpeaking(true);
        clearSilenceTimer();
      }

      if (final) {
        const updated = accumulatedRef.current ? accumulatedRef.current + ' ' + final : final;
        accumulatedRef.current = updated.trim();
        setTranscript(accumulatedRef.current);
        startSilenceTimer();
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: Event) => {
      const code = 'error' in event ? String((event as { error?: string }).error) : '';
      clearSilenceTimer();
      setIsListening(false);
      setIsSpeaking(false);
      // `aborted` is normal when we call stop(); otherwise clear so start() can create a new instance.
      if (code !== 'aborted') {
        recognitionRef.current = null;
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      setIsSpeaking(false);

      if (continuous && !stoppedManuallyRef.current) {
        recognitionRef.current = null;
        try {
          const retry = new Ctor();
          retry.continuous = true;
          retry.interimResults = true;
          retry.lang = 'en-US';
          retry.onresult = recognition.onresult;
          retry.onerror = recognition.onerror;
          retry.onend = recognition.onend;
          recognitionRef.current = retry;
          retry.start();
          setIsListening(true);
        } catch {
          // browser refused restart
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isSupported, continuous, clearSilenceTimer, startSilenceTimer]);

  const stop = useCallback(() => {
    stoppedManuallyRef.current = true;
    clearSilenceTimer();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimTranscript('');
    setIsSpeaking(false);
  }, [clearSilenceTimer]);

  const resetTranscript = useCallback(() => {
    accumulatedRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      stoppedManuallyRef.current = true;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          recognitionRef.current.stop();
        }
        recognitionRef.current = null;
      }
    };
  }, [clearSilenceTimer]);

  return {
    transcript,
    interimTranscript,
    isListening,
    isSpeaking,
    isSupported,
    start,
    stop,
    resetTranscript,
  };
}
